/**
 * useGalleryMutations — All gallery write operations (save, add, replace, remove)
 *
 * Extracted from ProductGalleryWidget to keep the component under 800 lines.
 * Contains: mutations, handlers, and the video replace flow.
 */

import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getERPClient } from '@/lib/erp/client';
import { type GalleryImage } from './SortableImageItem';
import { isVideoMedia } from './galleryMediaUtils';
import { normalizeFileUrl } from '@/lib/attachments/utils';
import type { GetAttachmentsResponse } from '@/lib/attachments/types';
import { getAttachmentsQueryKey } from '@/lib/attachments/hooks';
import { toast } from 'sonner';

// Re-export for convenience
export type { GalleryImage } from './SortableImageItem';

/** Video extension check for attachment filtering */
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v', '.flv', '.wmv', '.3gp'];

/** Decode URI safely to handle encoding mismatches (e.g. "classic%20m.jpg" vs "classic m.jpg") */
const safeDecodeURI = (s: string): string => {
  try { return decodeURIComponent(s); } catch { return s; }
};

const isVideoAttachment = (att: { file_url?: string | null; file_name?: string | null; mime_type?: string | null }) => {
  if (att.mime_type?.startsWith('video/')) return true;
  const fileName = (att.file_name || att.file_url || '').toLowerCase();
  return VIDEO_EXTENSIONS.some(ext => fileName.endsWith(ext));
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface DraftMediaItem {
  file_url: string;
  file_name: string;
  is_primary_image?: boolean;
  localPreviewUrl?: string;
  idx?: number;
}

interface UseGalleryMutationsOptions {
  itemCode: string;
  isDraftMode: boolean;
  galleryImages: GalleryImage[];
  videoCount: number;
  /** Parent template item_code — when set, gallery saves also invalidate the parent's variant summary cache */
  variantOf?: string;
  draftMedia?: DraftMediaItem[];
  onDraftReorder?: (media: DraftMediaItem[]) => void;
  onDraftSetPrimary?: (url: string) => void;
  onDraftRemoveMany?: (urls: string[]) => void;
  onDraftAddMedia?: (items: Array<{ file_url: string; file_name: string; is_primary_image: boolean }>) => void;
  docAttachments?: GetAttachmentsResponse;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useGalleryMutations({
  itemCode,
  isDraftMode,
  galleryImages,
  videoCount,
  variantOf,
  draftMedia,
  onDraftReorder,
  onDraftSetPrimary,
  onDraftRemoveMany,
  onDraftAddMedia,
}: UseGalleryMutationsOptions) {
  const queryClient = useQueryClient();
  const MAX_GALLERY_VIDEOS = 3;

  // Track URLs of recently-removed gallery images so handleAddImagesCompleted
  // doesn't re-add orphaned File attachments that the backend failed to clean up
  // (e.g. URL-encoded Thai filenames that _delete_removed_attachments can't match).
  const recentlyRemovedUrlsRef = useRef<Set<string>>(new Set());

  // Read fresh gallery images directly from the query cache to avoid stale-closure
  // bugs. The `galleryImages` prop is derived via useMemo in the parent and may lag
  // behind the query cache after addImagesMutation updates it (batch upload race).
  // Without this, deleting 1 image right after batch upload sends only pre-batch
  // images to save_item_gallery, causing the backend full-replace to delete the
  // entire batch. Evidence: BDL-outfit 14:25-14:26 timeline, 7→3 images.
  const getFreshGalleryImages = (): GalleryImage[] => {
    if (isDraftMode) return galleryImages;
    const cached = queryClient.getQueryData<{
      media?: Array<{
        name?: string; url?: string; alt?: string; type?: string;
        isPrimaryImage?: boolean; isPrimaryVideo?: boolean; idx?: number;
      }>;
    }>(['item-gallery', itemCode]);
    if (!cached?.media || !Array.isArray(cached.media)) return galleryImages;
    return cached.media.map((item) => ({
      name: item.name || '',
      image: item.url || '',
      alt_text: item.alt || '',
      media_type: item.type || 'image',
      is_primary: item.isPrimaryImage ? 1 : 0,
      is_primary_image: item.isPrimaryImage ? 1 : 0,
      is_primary_video: item.isPrimaryVideo ? 1 : 0,
      idx: item.idx || 0,
    } as GalleryImage));
  };

  // Attachment cleanup is handled server-side by _delete_removed_attachments
  // inside save_item_gallery — no client-side delete needed.

  // ── Save gallery (reorder / primary / full replace) ─────────────────────

  const saveGalleryMutation = useMutation({
    mutationFn: async (orderedImages: GalleryImage[]) => {
      if (!itemCode || isDraftMode) throw new Error('Item code is required');
      return await getERPClient().call(
        'zaviago_backend.api.item_gallery.save_item_gallery',
        {
          item_code: itemCode,
          gallery_json: JSON.stringify(
            orderedImages.map((img, index) => ({
              name: img.name,
              image: img.image,
              alt_text: img.alt_text,
              media_type: img.media_type || 'image',
              is_primary_image: img.is_primary_image ?? (img.is_primary ?? 0),
              is_primary_video: img.is_primary_video ?? 0,
              idx: index + 1,
            })),
          ),
        },
      );
    },
    onMutate: async (orderedImages: GalleryImage[]) => {
      if (!itemCode) return;
      await queryClient.cancelQueries({ queryKey: ['item-gallery', itemCode] });
      const previousData = queryClient.getQueryData(['item-gallery', itemCode]);
      queryClient.setQueryData(['item-gallery', itemCode], (old: Record<string, unknown> | undefined) => ({
        ...(old ?? {}),
        item_code: itemCode,
        media: orderedImages.map((img, index) => ({
          name: img.name,
          url: img.image,
          alt: img.alt_text,
          type: img.media_type || 'image',
          isPrimaryImage: !!(img.is_primary_image ?? img.is_primary ?? 0),
          isPrimaryVideo: !!(img.is_primary_video ?? 0),
          idx: index + 1,
          thumbnailUrl: null,
        })),
      }));
      return { previousData };
    },
    onError: (error, _variables, context) => {
      console.error('Failed to save gallery order:', error);
      if (context?.previousData && itemCode) {
        queryClient.setQueryData(['item-gallery', itemCode], context.previousData);
      }
    },
    onSettled: () => {
      if (!itemCode) return;
      queryClient.invalidateQueries({ queryKey: ['item-gallery', itemCode] });
      queryClient.invalidateQueries({ queryKey: getAttachmentsQueryKey({ doctype: 'Item', name: itemCode }) });
      // When a variant's gallery changes, the parent template's variant thumbnail (from Item.image) may update
      if (variantOf) {
        queryClient.invalidateQueries({ queryKey: ['template-variants-summary', variantOf] });
      }
    },
  });

  // ── Add images ──────────────────────────────────────────────────────────

  const addImagesMutation = useMutation({
    mutationFn: async (files: Array<{ file_url: string; file_name: string }>) => {
      if (!itemCode || isDraftMode) throw new Error('Item code is required');
      return await getERPClient().call(
        'zaviago_backend.api.item_gallery.add_images_from_files',
        { item_code: itemCode, files_json: JSON.stringify(files) },
      );
    },
    onSuccess: (data) => {
      if (!itemCode) return;
      queryClient.setQueryData(['item-gallery', itemCode], data);
      queryClient.invalidateQueries({ queryKey: ['item-gallery', itemCode] });
    },
    onError: (error) => {
      console.error('Failed to add images to gallery:', error);
      toast.error((error as Error)?.message || 'Failed to add images to gallery');
    },
  });

  // ── Add videos ──────────────────────────────────────────────────────────

  const addVideosMutation = useMutation({
    mutationFn: async (files: Array<{ file_url: string; file_name: string }>) => {
      if (!itemCode) throw new Error('Item code is required');
      return await getERPClient().call(
        'zaviago_backend.api.item_gallery.add_videos_from_files',
        { item_code: itemCode, files_json: JSON.stringify(files) },
      );
    },
    onSuccess: (data) => {
      if (!itemCode) return;
      queryClient.setQueryData(['item-gallery', itemCode], data);
      queryClient.invalidateQueries({ queryKey: ['item-gallery', itemCode] });
      queryClient.invalidateQueries({ queryKey: getAttachmentsQueryKey({ doctype: 'Item', name: itemCode }) });
    },
    onError: (error) => {
      const msg = (error as Error)?.message || '';
      if (msg.includes('Maximum 3 videos') || (msg.includes('Cannot add') && msg.includes('video(s)'))) {
        console.warn('[Gallery] Max videos limit reached:', msg);
        return;
      }
      console.error('[Gallery] Failed to add videos to gallery:', error);
    },
  });

  // ── Remove images (delete-by-ID) ────────────────────────────────────────
  // Sends only the selected row NAMES; the backend computes the remaining
  // gallery from the DB (source of truth). This makes delete immune to a stale
  // client array silently wiping un-tracked images (e.g. a just-uploaded photo
  // that hadn't landed in the local cache yet) — the "upload then it vanishes"
  // bug. Replaces the previous full-array-replace delete.

  const removeImagesMutation = useMutation({
    mutationFn: async (names: string[]) => {
      if (!itemCode || isDraftMode) throw new Error('Item code is required');
      return await getERPClient().call(
        'zaviago_backend.api.item_gallery.remove_gallery_images',
        { item_code: itemCode, names_json: JSON.stringify(names) },
      );
    },
    onSuccess: (data) => {
      if (!itemCode) return;
      queryClient.setQueryData(['item-gallery', itemCode], data);
      queryClient.invalidateQueries({ queryKey: ['item-gallery', itemCode] });
      queryClient.invalidateQueries({ queryKey: getAttachmentsQueryKey({ doctype: 'Item', name: itemCode }) });
      if (variantOf) {
        queryClient.invalidateQueries({ queryKey: ['template-variants-summary', variantOf] });
      }
    },
    onError: (error) => {
      console.error('Failed to remove gallery images:', error);
      toast.error((error as Error)?.message || 'Failed to remove images from gallery');
    },
  });

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleGalleryReorder = (newOrder: GalleryImage[]) => {
    if (isDraftMode && onDraftReorder && draftMedia) {
      const reorderedDraft = newOrder
        .map((img) => {
          const original = draftMedia.find((d) => {
            const normalized = normalizeFileUrl(d.file_url);
            const imgNormalized = normalizeFileUrl(img.image);
            return normalized === imgNormalized || d.file_url === img.image || d.localPreviewUrl === img.image;
          });
          return original ? { ...original, idx: img.idx ?? 0, is_primary_image: img.is_primary_image === 1 } : null;
        })
        .filter(Boolean) as typeof draftMedia;
      onDraftReorder(reorderedDraft);
      return;
    }
    if (!isDraftMode) saveGalleryMutation.mutate(newOrder);
  };

  const handleSetPrimary = (imageName: string, isVideo = false) => {
    const fresh = getFreshGalleryImages();
    if (!fresh?.length) return;

    if (isDraftMode && !isVideo && onDraftSetPrimary) {
      const target = fresh.find((img) => img.name === imageName);
      if (target) {
        const url = normalizeFileUrl(target.image) || target.image;
        onDraftSetPrimary(url);
      }
      return;
    }

    const updatedImages = fresh.map((img) => {
      if (isVideo) {
        const isActuallyVideo = isVideoMedia(img.media_type, img.image);
        return isActuallyVideo ? { ...img, is_primary_video: img.name === imageName ? 1 : 0 } : img;
      }
      const isActuallyVideo = isVideoMedia(img.media_type, img.image);
      return !isActuallyVideo ? { ...img, is_primary_image: img.name === imageName ? 1 : 0 } : img;
    });

    if (!isDraftMode) saveGalleryMutation.mutate(updatedImages);
  };

  const handleRemoveSelectedImages = async (selectedImages: Set<string>) => {
    const fresh = getFreshGalleryImages();
    if (!fresh?.length || selectedImages.size === 0) return;

    if (isDraftMode && onDraftRemoveMany) {
      const selectedUrls = fresh
        .filter((img) => selectedImages.has(img.name))
        .map((img) => normalizeFileUrl(img.image) || img.image);
      onDraftRemoveMany(selectedUrls);
      return;
    }

    const removedImages = fresh.filter((img) => selectedImages.has(img.name));

    // Track removed URLs so an in-flight upload callback from the SAME batch
    // doesn't immediately re-add an orphaned File attachment. A deliberate
    // later re-upload of the same file clears this guard (see
    // handleAddImagesCompleted), so re-uploading a deleted image still works.
    for (const img of removedImages) {
      const url = normalizeFileUrl(img.image);
      if (url) recentlyRemovedUrlsRef.current.add(safeDecodeURI(url));
    }

    // Delete-by-ID: send only the selected row names. The backend recomputes
    // the remaining gallery from the DB (source of truth) and re-assigns the
    // primary if needed — so a stale/incomplete local array can never wipe
    // images the user did not select (the batch-delete over-deletion bug).
    await removeImagesMutation.mutateAsync([...selectedImages]);

    // Backend remove_gallery_images cleans up the removed File attachments and
    // this mutation already invalidates the attachments query on success.
    queryClient.invalidateQueries({ queryKey: ['doc-sidebar', 'Item', itemCode] });
  };

  const handleAddImagesCompleted = (result?: GetAttachmentsResponse) => {
    if (!result?.attachments?.length) return;

    if (isDraftMode && onDraftAddMedia) {
      const newItems = result.attachments.map((att) => {
        const url = normalizeFileUrl(att.file_url) || att.file_url;
        return { file_url: url, file_name: att.file_name || url.split('/').pop() || 'image', is_primary_image: false };
      });
      onDraftAddMedia(newItems);
      return;
    }

    const normalizedFiles = result.attachments.map((att) => {
      const url = normalizeFileUrl(att.file_url) || att.file_url;
      return { file_url: url, file_name: att.file_name || url.split('/').pop() || 'image' };
    });

    // Build set of existing gallery URLs, decoded to handle encoding mismatches
    const existingUrls = new Set(
      getFreshGalleryImages()
        .map((img) => {
          const raw = normalizeFileUrl(img.image);
          return raw ? safeDecodeURI(raw) : null;
        })
        .filter(Boolean) as string[],
    );

    // Send files that are NOT already in the current gallery. A deliberate
    // re-upload of a file the user removed earlier this session MUST still work,
    // so clear any stale "recently removed" guard for it. That guard only exists
    // to stop an in-flight same-batch callback from resurrecting a just-deleted
    // image — it must not block an intentional re-upload (the reason repeated
    // re-uploads of the same filename silently did nothing).
    const newFiles = normalizedFiles.filter((f) => {
      const n = normalizeFileUrl(f.file_url);
      if (!n) return false;
      const decoded = safeDecodeURI(n);
      if (recentlyRemovedUrlsRef.current.has(decoded)) {
        recentlyRemovedUrlsRef.current.delete(decoded);
      }
      return !existingUrls.has(decoded);
    });

    if (newFiles.length > 0) addImagesMutation.mutate(newFiles);
  };

  const handleAddVideosCompleted = async (result?: GetAttachmentsResponse): Promise<boolean> => {
    if (!result || !itemCode) return false;

    const videoAttachments = (result.attachments || []).filter(isVideoAttachment);
    if (videoAttachments.length === 0) return false;

    const remainingSlots = MAX_GALLERY_VIDEOS - videoCount;
    if (remainingSlots <= 0) return false;

    const limitedVideos = videoAttachments.length > remainingSlots
      ? videoAttachments.slice(0, remainingSlots)
      : videoAttachments;

    const filesToAdd = limitedVideos
      .map((att) => ({
        file_url: normalizeFileUrl(att.file_url) || att.file_url || '',
        file_name: att.file_name || (normalizeFileUrl(att.file_url) || '').split('/').pop() || 'video',
      }))
      .filter((f) => f.file_url);

    if (filesToAdd.length === 0) return false;

    const existingVideoUrls = new Set(
      getFreshGalleryImages()
        .filter((img) => isVideoMedia(img.media_type, img.image))
        .map((img) => normalizeFileUrl(img.image))
        .filter(Boolean),
    );
    const newVideos = filesToAdd.filter((f) => !existingVideoUrls.has(f.file_url));
    if (newVideos.length === 0) return false;

    try {
      await addVideosMutation.mutateAsync(newVideos);
      return true;
    } catch {
      return false;
    }
  };

  // ── Replace media (OPS-1554 fix) ────────────────────────────────────────
  // Swaps a gallery item's URL in-place and saves via save_item_gallery.
  // The backend's save_item_gallery detects the removed URL and cleans up
  // the old attachment automatically.

  const handleReplaceMedia = async (
    targetName: string,
    result?: GetAttachmentsResponse,
  ): Promise<boolean> => {
    if (!result?.attachments?.length || !itemCode || isDraftMode) return false;

    const fresh = getFreshGalleryImages();
    const targetItem = fresh.find((img) => img.name === targetName);
    if (!targetItem) return false;

    const isTargetVideo = isVideoMedia(targetItem.media_type, targetItem.image);

    // Pick the first attachment that matches the target's media type
    const newAtt = result.attachments.find((att) => {
      if (isTargetVideo) return isVideoAttachment(att);
      return !isVideoAttachment(att);
    });
    if (!newAtt) return false;

    const newUrl = normalizeFileUrl(newAtt.file_url) || newAtt.file_url;
    if (!newUrl) return false;

    // Build updated gallery with the swapped URL
    const updatedImages = fresh.map((img) => {
      if (img.name !== targetName) return img;
      return {
        ...img,
        image: newUrl,
        alt_text: newAtt.file_name || img.alt_text,
        // Keep same primary status, idx, media_type
      };
    });

    try {
      await saveGalleryMutation.mutateAsync(updatedImages);
      return true;
    } catch {
      return false;
    }
  };

  return {
    saveGalleryMutation,
    addImagesMutation,
    addVideosMutation,
    handleGalleryReorder,
    handleSetPrimary,
    handleRemoveSelectedImages,
    handleAddImagesCompleted,
    handleAddVideosCompleted,
    handleReplaceMedia,
  };
}
